import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { Conversation, MessageInfo, WebChatPreviewOnly, WebChatThemeProps } from "@42maru/webchat";
import { API_HOST, WEBCHAT_HOST, WEBCHAT_SOCKET_PATH } from "@/core/variables";
import { MessageHistorySearchType } from "@/model";
import { mergeThemeWithDefault } from "@/util/helper/webchatThemeHelper";
import { useConversationMessagesByPosition, usePaginatedConversationMessages, useSearchedMessages } from "@/query/conversation";
import { useScenario } from "@/query/scenario";
import Button from "@/component/Button";
import { PageNextIcon, PagePreviousIcon } from "@/assets/svgs/icons";


const CONVERSATION_MESSAGE_SIZE = 20;

const toSenderType = (searchType: MessageHistorySearchType) => {
    switch (searchType) {
        case "user_query":
            return "user";
        case "bot_query":
            return "bot";
        case "conversation_id":
        case "user_key":
        default:
            return undefined;
    }
};

interface ConversationMessageListProps {
    conversation: Conversation;
    searchKeyword: string;
    searchType: MessageHistorySearchType;
}

const ConversationMessageList: React.FC<ConversationMessageListProps> = (props) => {
    const { conversation, searchKeyword, searchType } = props;
    const senderType = useMemo(() => toSenderType(searchType), [searchType]);
    
    const intl = useIntl();

    const [pageToLoad, setPageToLoad] = useState<number>(0);
    const [searchIndex, setSearchIndex] = useState<number>();
    const [markedId, setMarkedId] = useState<string>();

    // not automatically refetched
    const {
        data: messagesPages,
        fetchNextPage,
        fetchPreviousPage,
        isFetching,
        hasNextPage,
        hasPreviousPage,
        refetch: refetchMessagesPages
    } = usePaginatedConversationMessages({
        scenarioId: conversation.scenario_id,
        conversationId: conversation.id,
        page: pageToLoad,
        size: CONVERSATION_MESSAGE_SIZE
    });
    const { data: searchedMessages } = useSearchedMessages({
        scenarioId: conversation.scenario_id,
        conversationId: conversation.id,
        senderType,
        condition: searchKeyword
    });
    // not automatically refetched
    const { refetch: refetchFocusedMessagePage } = useConversationMessagesByPosition({
        scenarioId: conversation.scenario_id,
        conversationId: conversation.id,
        senderType,
        condition: searchKeyword,
        position: searchIndex,
        size: CONVERSATION_MESSAGE_SIZE
    });

    const onEnterTop = () => {
        if (hasNextPage) {
            setMarkedId(undefined);
            fetchNextPage();
        }
    };

    const onEnterBottom = () => {
        if (hasPreviousPage) {
            setMarkedId(undefined);
            fetchPreviousPage();
        }
    };

    const onMovePosition = (index: number) => {
        if (!searchedMessages || index < 0 || index >= searchedMessages.length) {
            return;
        }
        setSearchIndex(index);
    };

    const messages = useMemo(() => {
        const filterMessage = (messageInfo: MessageInfo) => {
            if (!messageInfo.messages || messageInfo.messages.length !== 1) {
                return true;
            }
            const message = messageInfo.messages[0];
            const { data_type, data } = message;
            if (data_type === "custom" && data && (data as any).type === "custom-ui-popup") {
                return false;
            }
            return true;
        };
        return messagesPages?.pages.flatMap(page => page.content).filter(filterMessage).reverse() ?? [];
    }, [messagesPages]);

    useEffect(() => {
        setSearchIndex(undefined);
        setPageToLoad(0);
    }, [searchKeyword, senderType]);

    useEffect(() => {
        if (!searchedMessages || !senderType) {
            return;
        }
        setSearchIndex(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchedMessages]);

    useEffect(() => {
        if (searchIndex === undefined) {
            setMarkedId(undefined);
            return;
        }

        refetchFocusedMessagePage()
            .then(({ data: focusedMessagePage }) => {
                if (!focusedMessagePage) {
                    return;
                }
                if (messagesPages?.pages.some(page => page.page === focusedMessagePage.page)) {
                    setMarkedId(searchedMessages?.[searchIndex]?.id);
                } else {
                    setPageToLoad(focusedMessagePage.page);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchIndex]);

    useEffect(() => {
        refetchMessagesPages()
            .then(() => {
                if (searchedMessages?.length && searchIndex !== undefined) {
                    setMarkedId(searchedMessages?.[searchIndex]?.id);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation.scenario_id, conversation.id, pageToLoad]);

    return (
        <div className="max-h-full flex-1 flex flex-col gap-2">
            <ChatFeedContainer maxHeight={640}
                               scenarioId={conversation.scenario_id}
                               messages={messages}
                               searchedMessages={searchedMessages}
                               focusedMessageId={markedId}
                               onEnterTop={onEnterTop}
                               onEnterBottom={onEnterBottom}
                               isLoading={isFetching}
                               keyword={searchKeyword}
            />
            
            {searchedMessages && searchIndex !== undefined && (
                <div className="w-full flex justify-end items-center gap-3">
                    <Button className="btn-pagination"
                            aria-label={intl.formatMessage({ id: "b0064" })}
                            disabled={searchIndex === 0}
                            onClick={() => onMovePosition(searchIndex - 1)}
                    >
                        <PagePreviousIcon aria-hidden="true"
                                          className="size-full"
                        />
                    </Button>

                    <div aria-live="polite"
                         aria-atomic="true"
                         className="flex items-center gap-1 text-black-400 font-[500] text-[12px] leading-[1.4]"
                    >
                        <span>{searchIndex + 1}</span>
                        <span>/</span>
                        <span>{searchedMessages?.length}</span>
                    </div>

                    <Button className="btn-pagination"
                            aria-label={intl.formatMessage({ id: "b0065" })}
                            disabled={searchIndex + 1 === searchedMessages?.length}
                            onClick={() => onMovePosition(searchIndex + 1)}
                    >
                        <PageNextIcon aria-hidden="true"
                                      className="size-full"
                        />
                    </Button>
                </div>
            )}
        </div>
    );
};

interface ChatFeedContainerProps {
    scenarioId: string;
    messages: MessageInfo[];
    maxHeight: number;
    searchedMessages?: MessageInfo[];
    focusedMessageId?: string;
    isLoading: boolean;
    onEnterTop: () => void;
    onEnterBottom: () => void;
    keyword: string;
}

const ChatFeedContainer: React.FC<ChatFeedContainerProps> = (props) => {
    const { scenarioId, messages, maxHeight, searchedMessages, focusedMessageId, isLoading, onEnterTop, onEnterBottom, keyword } = props;

    const intl = useIntl();
    const { data: scenarioData } = useScenario(scenarioId);

    const webchatTheme = useMemo<WebChatThemeProps>(() => {
        return mergeThemeWithDefault(scenarioData?.publish?.webchat_theme);
    }, [scenarioData?.publish?.webchat_theme]);

    const activeMessageIds = useMemo(() => {
        return searchedMessages?.map(message => message.id).sort((a, b) => {
            if (a === focusedMessageId) {
                return -1;
            }
            if (b === focusedMessageId) {
                return 1;
            }
            return 0;
        }) ?? [];
    }, [searchedMessages, focusedMessageId]);

    if (!messages.length || (keyword && !searchedMessages?.length)) {
        return null;
    }
    return (
        <div role="region"
             aria-label={intl.formatMessage({ id: "i07002" })}
             className="h-full flex flex-col border border-black-200 rounded-[6px] overflow-hidden"
             style={{ maxHeight }}
        >
            <WebChatPreviewOnly locale={scenarioData?.properties?.language ?? "ko"}
                                messageInfos={messages}
                                windowWidth={"100%"}
                                className={classNames("h-full rounded-none", isLoading && "cursor-progress")}
                                theme={{
                                    ...webchatTheme,
                                    previewSettings: {
                                        headerLRMargin: 24,
                                    },
                                    showFeedbackRating: true
                                }}
                                clientType="chat_only"
                                footerType="sitebunny"
                                connectionConfig={{
                                    apiHost: API_HOST,
                                    socketHost: WEBCHAT_HOST,
                                    socketPath: WEBCHAT_SOCKET_PATH,
                                    method: "socket",
                                    test: true,
                                    hideDebugMessage: true
                                }}
                                scenarioId={scenarioId}
                                disableInteraction
                                loadPrev={onEnterTop}
                                loadNext={onEnterBottom}
                                activeMessageIds={activeMessageIds}
                                highlight={keyword}
                                isChatAlignBottom={false}
            />
        </div>
    );
};

export default ConversationMessageList;
